import { Tooltip } from 'antd';
import {
  formatINR,
  ErpDataGridStatusTag
} from '../../../../components/common/erp-grid';
import { describeItemFlags, classifyItemKind } from './itemMasterHelpers';
import { humaniseEnum } from './studioConstants';

// ─────────────────────────────────────────────────────────────────
// Glass-i — Item Master grid columns (Stage 6B.8A-4 · UX redesign).
//
// Single-row flat header. Every `dataIndex` maps to the camelCase
// keys emitted by `items.mapper.js → fromRow` on the slim list
// endpoint (`GET /api/erp/masters/items`). Extension tables are NOT
// joined into this list — columns surface only core + joined-lookup
// fields (category, material, UOM).
//
// Design language mirrors the Customer Master redesign: a standing
// stripe on the Item anchor, calm mono identifiers, plain coloured
// categoricals, a compact flag strip, and a grouped tabular price
// block. All cell styling is scoped under `.imx-grid` in ItemMaster.css
// so the shared `.erp-grid` baseline (used by /invoices, /users) is
// untouched.
// ─────────────────────────────────────────────────────────────────

const INR0 = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

function renderMutedDash() {
  return <span className="erp-grid__muted">—</span>;
}

// Row standing → the identity stripe colour, driven by item status.
//   active → green · inactive → grey · discontinued → red
function itemStanding(record) {
  const s = String(record?.status || '').toLowerCase();
  if (s === 'discontinued') return 'stop';
  if (s === 'inactive')     return 'off';
  return 'ok';
}

// Calm mono identifier (SKU / barcode / HSN / UOM) — slate, tabular.
function mono(value) {
  if (!value) return renderMutedDash();
  return <span className="imx-mono">{value}</span>;
}

// Right-aligned money — muted ₹, tabular; zero renders as a quiet dash
// so a column of blank rates reads calm rather than "₹0 ₹0 ₹0".
function money(value) {
  const n = Number(value) || 0;
  if (n <= 0) return <span className="imx-money imx-money--zero">—</span>;
  return (
    <span className="imx-money">
      <span className="imx-money__rs">₹</span>{INR0.format(n)}
    </span>
  );
}

// Per-page S.No — the grid passes (value, record, page-relative idx);
// we close over current page + pageSize for a cross-page serial number.
function buildSerialColumn({ currentPage, pageSize }) {
  return {
    title: '#',
    key: '__sno',
    width: 32,
    fixed: 'left',
    align: 'right',
    className: 'imx-col-sno',
    render: (_v, _r, idx) =>
      <span className="imx-sno">{(currentPage - 1) * pageSize + idx + 1}</span>
  };
}

// Compact flag strip — flat mono badges (S / L / P / M / J / C) instead
// of the loud AntD colour tags. Only "on" flags render; each keeps its
// tooltip. A quiet, scannable capability fingerprint per row.
function renderFlags(record) {
  const flags = describeItemFlags(record).filter((f) => f.on);
  const kind = classifyItemKind(record);
  if (flags.length === 0 && kind === 'none') return renderMutedDash();
  return (
    <span className="imx-flags">
      {flags.map((f) => (
        <Tooltip key={f.key} title={f.tooltip}>
          <span className={`imx-flag imx-flag--${f.key}`}>{f.letter}</span>
        </Tooltip>
      ))}
    </span>
  );
}

export function buildItemColumns({ currentPage = 1, pageSize = 25 } = {}) {
  const sno = buildSerialColumn({ currentPage, pageSize });

  // ── Item anchor — standing stripe + name over a mono SKU sub-line,
  // with quiet Derived / Set markers inline. GSTIN-style loud tags are
  // gone; the markers are flat text badges that don't fight the name. ──
  const item = {
    title: 'Item',
    key: 'itemName',
    dataIndex: 'itemName',
    sortField: 'item_name',
    sorter: true,
    width: 300,
    fixed: 'left',
    dataType: 'string',
    render: (v, record) => {
      const r = record || {};
      const derived = Boolean(r.sourceStockItemId);
      const isSet   = Boolean(r.isSetItem);
      const compCount = r.componentCount ? Number(r.componentCount) : 0;
      // Sub-line shows the SKU only — the internal product/item code is
      // intentionally hidden from the grid (still on the View/Edit popup).
      const sub = r.skucode || '';
      return (
        <span className={`imx-id imx-id--${itemStanding(r)}`}>
          <span className="imx-id__stripe" aria-hidden="true" />
          <span className="imx-id__block">
            <span className="imx-id__row">
              <span className="imx-id__nm" title={v || ''}>{v || '—'}</span>
              {derived ? (
                <Tooltip title="Derived item — a finished or processed variant made from another stock material.">
                  <span className="imx-badge imx-badge--derived">DERIVED</span>
                </Tooltip>
              ) : null}
              {isSet ? (
                <Tooltip title={`Set item — contains ${compCount} component${compCount === 1 ? '' : 's'}. Use "View Components" on the row menu.`}>
                  <span className="imx-badge imx-badge--set">SET · {compCount}</span>
                </Tooltip>
              ) : null}
            </span>
            {sub ? <span className="imx-id__sub">{sub}</span> : null}
          </span>
        </span>
      );
    }
  };

  const barcode = {
    title: 'Barcode',
    key: 'barcode',
    dataIndex: 'barcode',
    sortField: 'barcode',
    sorter: true,
    width: 140,
    dataType: 'string',
    render: (v) => mono(v)
  };

  const category = {
    title: 'Category',
    key: 'itemCategoryName',
    dataIndex: 'itemCategoryName',
    sortField: 'item_category_name',
    sorter: true,
    width: 150,
    ellipsis: true,
    dataType: 'string',
    groupable: true,
    groupField: 'item_category_name',
    render: (v) => v ? <span className="imx-cat">{v}</span> : renderMutedDash()
  };

  const material = {
    title: 'Material',
    key: 'materialTypeName',
    dataIndex: 'materialTypeName',
    sortField: 'material_type_name',
    sorter: true,
    width: 140,
    ellipsis: true,
    dataType: 'string',
    groupable: true,
    groupField: 'material_type_name',
    render: (v) => v ? <span className="imx-mat">{v}</span> : renderMutedDash()
  };

  const uom = {
    title: 'UOM',
    key: 'defaultUomCode',
    dataIndex: 'defaultUomCode',
    sortField: 'default_uom_code',
    sorter: true,
    width: 78,
    dataType: 'string',
    render: (v) => mono(v)
  };

  const hsn = {
    title: 'HSN / SAC',
    key: 'hsncode',
    dataIndex: 'hsncode',
    sortField: 'hsn_sac',
    sorter: true,
    width: 104,
    dataType: 'string',
    render: (v) => mono(v)
  };

  // GST % with a small tabular treatment and a taxability tooltip.
  const gst = {
    title: 'GST',
    key: 'gstRatePercent',
    dataIndex: 'gstRatePercent',
    sortField: 'gst_rate_percent',
    sorter: true,
    width: 74,
    align: 'right',
    dataType: 'number',
    render: (v, record) => {
      const tax = record && record.taxabilityType;
      const pct = (v === null || v === undefined) ? null : Number(v);
      if (pct === null) return renderMutedDash();
      return (
        <Tooltip title={tax ? `Taxability: ${humaniseEnum(tax)}` : ''}>
          <span className="imx-gst">{pct.toFixed(pct % 1 === 0 ? 0 : 2)}%</span>
        </Tooltip>
      );
    }
  };

  const sales = {
    title: 'Sales',
    key: 'salesRate',
    dataIndex: 'salesRate',
    sortField: 'sales_rate',
    sorter: true,
    width: 108,
    align: 'right',
    dataType: 'number',
    render: (v) => money(v)
  };

  const purchase = {
    title: 'Purchase',
    key: 'purchaseRate',
    dataIndex: 'purchaseRate',
    sortField: 'purchase_rate',
    sorter: true,
    width: 108,
    align: 'right',
    dataType: 'number',
    render: (v) => money(v)
  };

  const mrp = {
    title: 'MRP',
    key: 'mrp',
    dataIndex: 'mrp',
    sortField: 'mrp',
    sorter: true,
    width: 104,
    align: 'right',
    dataType: 'number',
    render: (v) => money(v)
  };

  const flags = {
    title: 'Capabilities',
    key: '__flags',
    width: 150,
    render: (_v, record) => renderFlags(record)
  };

  const status = {
    title: 'Status',
    key: 'status',
    dataIndex: 'status',
    sortField: 'status',
    sorter: true,
    width: 104,
    align: 'center',
    dataType: 'enum',
    groupable: true,
    groupField: 'status',
    filters: [
      { text: 'Active',       value: 'active' },
      { text: 'Inactive',     value: 'inactive' },
      { text: 'Discontinued', value: 'discontinued' }
    ],
    filterField: 'status',
    render: (v) => <ErpDataGridStatusTag value={v} />
  };

  // ── Column order — S.No, the Item anchor (name + SKU), identity
  // (Barcode, HSN, UOM), classification (Category, Material), the tax +
  // grouped price block (GST, Sales, Purchase, MRP), the capability
  // strip, and Status closes the row. ──
  return [
    sno, item, barcode, category, material, uom, hsn,
    gst, sales, purchase, mrp, flags, status
  ];
}

// Read-only sections used by the quick-view drawer. Keep these short
// — the drawer is a glance surface, not a full record editor.
export function buildItemDetailGroups() {
  const fallback = (v) => v || '—';
  return [
    {
      title: 'Identity',
      items: [
        { label: 'Code', dataIndex: 'itemCode',
          render: (v) => v ? <code className="erp-grid__code">{v}</code> : '—' },
        { label: 'Name', dataIndex: 'itemName' },
        { label: 'SKU', dataIndex: 'skucode',
          render: (v) => v ? <code className="erp-grid__code">{v}</code> : '—' },
        { label: 'Barcode', dataIndex: 'barcode',
          render: (v) => v ? <code className="erp-grid__code">{v}</code> : '—' },
        { label: 'Item type', dataIndex: 'itemType', render: (v) => humaniseEnum(v) || '—' },
        { label: 'Status', dataIndex: 'status', render: (v) => v ? humaniseEnum(v) : '—' }
      ]
    },
    {
      title: 'Classification',
      items: [
        { label: 'Category', dataIndex: 'itemCategoryName', render: fallback },
        { label: 'Material', dataIndex: 'materialTypeName', render: fallback },
        { label: 'Material family', dataIndex: 'materialFamily',
          render: (v) => humaniseEnum(v) || '—' }
      ]
    },
    {
      title: 'GST / UOM / Pricing',
      items: [
        { label: 'HSN/SAC', dataIndex: 'hsncode',
          render: (v) => v ? <code className="erp-grid__code">{v}</code> : '—' },
        { label: 'GST %', dataIndex: 'gstRatePercent',
          render: (v) => (v === null || v === undefined) ? '—' : `${Number(v).toFixed(2)}%` },
        { label: 'Taxability', dataIndex: 'taxabilityType',
          render: (v) => humaniseEnum(v) || '—' },
        { label: 'Default UOM', dataIndex: 'defaultUomCode', render: fallback },
        { label: 'Default UQC', dataIndex: 'defaultUqcCode', render: fallback },
        { label: 'Sales rate', dataIndex: 'salesRate',
          render: (v) => formatINR(v ?? 0) },
        { label: 'Purchase rate', dataIndex: 'purchaseRate',
          render: (v) => formatINR(v ?? 0) },
        { label: 'MRP', dataIndex: 'mrp',
          render: (v) => formatINR(v ?? 0) }
      ]
    },
    {
      title: 'Source stock material',
      // Only useful for derived rows.
      items: [
        { label: 'Linked', dataIndex: 'sourceStockItemId',
          render: (v) => v ? 'Yes — derived item' : 'No (this row is base or stand-alone)' },
        { label: 'Source code', dataIndex: 'sourceStockItemCode',
          render: (v) => v ? <code className="erp-grid__code">{v}</code> : '—' },
        { label: 'Source name', dataIndex: 'sourceStockItemName', render: fallback }
      ]
    }
  ];
}
